import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

/**
 * Hook to safely navigate back, checking if the previous page was from the same website.
 * If the previous page was from an external site, navigates to home instead.
 */
export function useSafeBackNavigation() {
  const router = useRouter();

  /**
   * Checks if the previous page was from the same origin/website
   */
  const isPreviousPageFromSameSite = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;

    // If there's no history to go back to, definitely not from same site
    if (window.history.length <= 1) {
      return false;
    }

    // Check if there's a referrer and if it's from the same origin
    const referrer = document.referrer;
    if (!referrer) {
      // No referrer could mean:
      // 1. User typed URL directly (should go to home)
      // 2. User opened in new tab (should go to home)
      // 3. User came from external site but referrer was blocked (should go to home)
      // However, if history.length > 1, there might be internal navigation
      // In this case, we'll be conservative and assume it's safe to go back
      return window.history.length > 1;
    }

    try {
      const referrerUrl = new URL(referrer);
      const currentUrl = new URL(window.location.href);
      
      // Check if referrer is from the same origin (same protocol, host, and port)
      return referrerUrl.origin === currentUrl.origin;
    } catch (error) {
      // If URL parsing fails, be conservative and assume it's external
      return false;
    }
  }, []);

  /**
   * Safely navigates back if previous page was from same site, otherwise navigates to home
   */
  const safeBack = useCallback(
    (homePath: string = '/') => {
      if (isPreviousPageFromSameSite()) {
        router.back();
      } else {
        router.push(homePath);
      }
    },
    [router, isPreviousPageFromSameSite]
  );

  return {
    safeBack,
    isPreviousPageFromSameSite,
  };
}
